import isValue from '@glass/common/modules/utils/isValue';

/**
 * Options for counting object values.
 */
interface CountOptions {
  /**
   * Keys to ignore during the count.
   */
  ignoreKeys?: string[];
  validStringLength?: number;
}

/**
 * Recursively counts the number of valid values in an object.
 * A valid value is one that is not undefined, null, or an empty string.
 * Additionally, strings longer than 10 characters are counted as 1.
 *
 * @param obj - The object to count values in.
 * @param options - Optional settings to customize the count.
 * @returns The total count of valid values.
 */
export function countObjectValues(obj: unknown, options: CountOptions = {}): number {
  const { ignoreKeys = [], validStringLength = 7 } = options;

  if (typeof obj === 'string') {
    return obj.length > validStringLength ? 1 : 0;
  }

  if (typeof obj === 'number') {
    return 1;
  }

  if (!isValue(obj) || typeof obj !== 'object') {
    return 0;
  }

  // If obj is an array, recursively count each item
  if (Array.isArray(obj)) {
    return (obj as unknown[]).reduce(
      (count: number, item) => count + countObjectValues(item, options),
      0,
    );
  }

  // If obj is a plain object, iterate through its keys
  return Object.entries(obj as Record<string, unknown>).reduce((count, [key, value]) => {
    // Ignore specified keys
    if (ignoreKeys.includes(key)) {
      return count;
    }

    return count + countObjectValues(value, options);
  }, 0);
}

export default countObjectValues;
